import {
  Result, ok, err,
} from 'neverthrow';
import download from 'downloadjs';
import i18n from '@/i18n';
import { errorTracker } from '@/utils/mixins/analytics';
import { pushProgress, popProgress } from '@/utils/top-progress';

const EMPTY_MSG = '<empty>';

type ResponseMessage = {
  key?: string,
  value?: string,
  args?: string[]
}

interface DownloadFileRequest {
  endpoint: string
}

function resolveMessage(message?: ResponseMessage): string {
  if (!message) {
    return EMPTY_MSG;
  }
  if (!message.key || !i18n.te(`mycountly.responses.${message.key}`)) {
    return message.value || EMPTY_MSG;
  }
  return i18n.t(`mycountly.responses.${message.key}`, message.args).toString() || EMPTY_MSG;
}

export interface GenericResponse {
  code: number,
  message?: string,
  payload?: any
}

export type HTTPResultAsync<T = GenericResponse, E = Error> = Promise<Result<T, E>>

class HTTPError extends Error {
  constructor(
    public message: string,
    public code: number,
    public payload?: any,
  ) {
    super(message);
  }
}

async function send(method: 'POST'|'GET'|'PUT'|'DELETE'|'PATCH', endpoint: string, body?: object, headers?: object, files?: { [x: string]: File; }): Promise<GenericResponse> {
  // prepare request object
  let options: any = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  if (files && Object.keys(files).length) {
    const fd = new FormData();
    Object.keys(files).forEach((key) => fd.append(key, files[key]));
    if (body) {
      Object.keys(body).forEach((key) => {
        fd.append(key, (body as any)[key]);
      });
    }
    options = {
      method,
      body: fd,
    };
  } else if (body) {
    options = {
      method,
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    };
  }

  if (typeof headers !== 'undefined') {
    Object.assign(options.headers, headers);
  }

  pushProgress();
  let response: Response;
  try {
    response = await fetch(`/api/v1${endpoint}`, options);
  } finally {
    popProgress();
  }
  let parsedResponse: {
    message?: ResponseMessage,
    payload?: any
  };
  try {
    parsedResponse = await response.json();
  } catch {
    parsedResponse = {};
  }

  let message = resolveMessage(parsedResponse.message);

  if (response.status < 200 || response.status >= 400) {
    if (parsedResponse?.payload?.errors) {
      const errorFields = Object.keys(parsedResponse.payload.errors);
      let messageConcat = '';
      errorFields.forEach((errorField, index) => {
        const errorMessage = parsedResponse.payload.errors[errorField][Object.keys(parsedResponse.payload.errors[errorField])[0]];
        messageConcat += errorMessage + (index !== errorFields.length - 1 ? ', ' : '.');
      });
      message = messageConcat;
    }

    if (response.status >= 500) {
      errorTracker(message);
    }

    throw new HTTPError(
      `${message}`,
      response.status,
      parsedResponse.payload,
    );
  }

  return {
    message,
    code: response.status,
    payload: parsedResponse.payload,
  };
}

async function sendSafe(method: 'POST'|'GET'|'PUT'|'DELETE'|'PATCH', endpoint: string, body?: object, headers?: object, files?: { [x: string]: File; }): HTTPResultAsync {
  try {
    const success = await send(method, endpoint, body, headers, files);
    return ok(success);
  } catch (ex) {
    return err(ex as Error);
  }
}

async function get(endpoint: string, headers: { [key: string]: string }): Promise<GenericResponse> {
  const response = await fetch(endpoint, { headers });

  let parsedResponse: {
    message?: ResponseMessage,
    payload?: any
  };

  try {
    parsedResponse = await response.json();
  } catch {
    parsedResponse = {};
  }

  if (response.status < 200 || response.status >= 400) {
    throw (response);
  }

  return {
    code: response.status,
    payload: parsedResponse,
  };
}

async function getSafe(endpoint: string, headers: { [key: string]: string }) {
  try {
    const success = await get(endpoint, headers);
    return ok(success);
  } catch (ex) {
    return err(ex as Error);
  }
}

export async function downloadFile(payload: DownloadFileRequest) {
  const now = new Date();
  const requestOptions: any = {
    method: 'GET',
  };

  const response = await fetch(`/api/v1${payload.endpoint}`, requestOptions)
    .then((resp) => resp.blob())
    .then((blob) => {
      download(
        blob,
        `countly-flex-export-${now.getDate()}-${
          now.getMonth() + 1
        }-${now.getFullYear()}__${now.getHours()}-${now.getMinutes()}.json`,
        'text/json',
      );
    });

  return response;
}

export default {
  send,
  sendSafe,
  get,
  getSafe,
  downloadFile,
};
